import { Dialog, Transition } from '@headlessui/react';
import Attention from 'assets/icons/Attention';
import Right from 'assets/icons/Right';
import Button from 'common/components/Button';
import Loader from 'common/components/Loader';
import useNavigate from 'common/hooks/useNavigate';
import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { removeCompanyById } from 'common/slices/auth.slice';
import { useDeleteCompanyMutation } from 'common/slices/companyApi.slice';
import { cn } from 'common/utils';
import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';

const DeleteCompanyModal = ({
  currentCompanyId,
  currentCompanyName,
  isOpen,
  setIsOpen,
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation('settings\\index');

  const [deleteCompany, { isLoading: isDeletingCompany }] =
    useDeleteCompanyMutation();

  const handleCompanySoftDelete = async () => {
    try {
      await deleteCompany({
        companyId: currentCompanyId,
        deleteFlag: true,
      }).unwrap();
      dispatch(removeCompanyById(currentCompanyId));
      toast.newToast(
        'positive',
        t('toasts.successful-delete-company', {
          companyName: currentCompanyName,
          interpolation: { escapeValue: false },
        }),
      );
      navigate('company-selector');
    } catch (err) {
      switch (err?.status) {
        case 403:
          toast.newToast('negative', t('toasts.forbidden'));
          break;
        default:
          toast.newToast('negative', t('toasts.something-wrong'));
      }
    } finally {
      closeModal();
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <Fragment>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={isDeletingCompany ? () => null : closeModal}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </Transition.Child>
          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel
                  className={cn(
                    'flex flex-col w-full max-w-md transform overflow-hidden',
                    'rounded bg-white p-10 text-left gap-6',
                    'align-middle shadow-xl transition-all relative',
                  )}
                >
                  <Dialog.Title
                    as='div'
                    className='flex flex-col w-full items-center gap-8'
                  >
                    <Attention size='64px' color='#084D6E' />
                    <h1 className=' text-2xl font-semibold text-dark-charcoal'>
                      {t(
                        'advanced-tab-sections.deactivate-company.modal.title',
                      )}
                    </h1>
                  </Dialog.Title>
                  <Dialog.Description className='text-sm text-cool-grey text-center'>
                    {t(
                      'advanced-tab-sections.deactivate-company.modal.description',
                    )}
                  </Dialog.Description>
                  <div className='w-full flex items-center justify-center gap-2'>
                    <Button variant='secondary' onClick={closeModal}>
                      {t(
                        'advanced-tab-sections.deactivate-company.modal.buttons.cancel',
                      )}
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleCompanySoftDelete}
                      disabled={isDeletingCompany}
                    >
                      {t(
                        'advanced-tab-sections.deactivate-company.modal.buttons.deactivate',
                      )}
                      <Right size='16px' color='#FFFFFF' />
                    </Button>
                  </div>
                  {isDeletingCompany && <Loader />}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
};

export default DeleteCompanyModal;
